import { useRef, useEffect } from 'react';

type UseDataLoadingStateProps = {
    isLoading: boolean;
    isLoaded: boolean;
    isError: boolean;
    onLoaded?: () => void;
    onError?: () => void;
};

/**
 * Custom hook to manage data loading state and trigger callbacks on load or error.
 *
 * @param isLoading - Indicates if the data is currently loading.
 * @param isLoaded - Indicates if the data has successfully loaded.
 * @param isError - Indicates if there was an error during loading.
 * @param onLoaded - Callback function to be called when data has loaded successfully.
 * @param onError - Callback function to be called when there was an error during loading.
 */

export const useDataLoadingState = ({ isLoading, isLoaded, isError, onLoaded, onError }: UseDataLoadingStateProps) => {
    const wasLoading = useRef(isLoading);

    useEffect(() => {
        if (isLoading) {
            wasLoading.current = true;
        }
    }, [isLoading]);

    useEffect(() => {
        const hadWasLoading = wasLoading.current;

        if (isLoaded && hadWasLoading) {
            wasLoading.current = false;
            onLoaded?.();
        }
        if (isError && hadWasLoading) {
            wasLoading.current = false;
            onError?.();
        }
    }, [isLoading, isLoaded, isError]);
};
